import { Toggle } from '@base-ui/react/toggle';
import { ToggleGroup } from '@base-ui/react/toggle-group';
import { cva } from 'class-variance-authority';
import type { ReactNode } from 'react';

import { cn } from '../lib/cn';

export const segmentedControlVariants = cva(
  'border-line inline-flex items-center gap-1 rounded-lg border p-1',
);

const itemSizeClassName = {
  sm: 'px-snug py-0.5 text-xs',
  md: 'px-row py-tight text-sm',
} as const;

export interface SegmentedControlOption<T extends string> {
  value: T;
  label: ReactNode;
  disabled?: boolean;
}

export interface SegmentedControlProps<T extends string> {
  value: T;
  onValueChange: (value: T) => void;
  options: ReadonlyArray<SegmentedControlOption<T>>;
  'aria-label': string;
  size?: 'sm' | 'md';
  className?: string;
}

/**
 * 서로 배타적인 몇 개 보기 사이의 축척·모드 전환 (예: 주간/월간).
 * 단일 선택을 강제한다 — 눌린 항목을 다시 눌러도 선택이 비지 않는다.
 */
export function SegmentedControl<T extends string>({
  value,
  onValueChange,
  options,
  'aria-label': ariaLabel,
  size = 'md',
  className,
}: SegmentedControlProps<T>) {
  return (
    <ToggleGroup
      aria-label={ariaLabel}
      value={[value]}
      onValueChange={(next) => {
        // 그룹의 마지막 항목을 다시 누르면 빈 배열이 온다 — 무시해서 항상 하나는 눌려 있게 한다.
        const [nextValue] = next as T[];
        if (nextValue !== undefined) onValueChange(nextValue);
      }}
      className={cn(segmentedControlVariants(), className)}
    >
      {options.map((option) => (
        <Toggle
          key={option.value}
          value={option.value}
          disabled={option.disabled}
          className={cn(
            'text-ink-muted rounded-md font-medium whitespace-nowrap',
            'ease-flow duration-fast transition-colors',
            'hover:bg-highlight',
            'data-[pressed]:bg-selected data-[pressed]:text-selected-fg',
            'disabled:pointer-events-none disabled:opacity-50',
            itemSizeClassName[size],
          )}
        >
          {option.label}
        </Toggle>
      ))}
    </ToggleGroup>
  );
}
